import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import { Loader2, Plus, X, Trash2, Activity, ImagePlus, CheckCircle2, AlertCircle, Star, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useWorkspaceBranding } from "../contexts/WorkspaceBrandingContext";
import { useUnsavedChanges } from "../contexts/NavigationGuardContext";

const DAYS = [
    { key: "mon", label: "Monday" },
    { key: "tue", label: "Tuesday" },
    { key: "wed", label: "Wednesday" },
    { key: "thu", label: "Thursday" },
    { key: "fri", label: "Friday" },
    { key: "sat", label: "Saturday" },
    { key: "sun", label: "Sunday" },
];

const LIMITS = {
    cta: 50,
    promo: 120,
    serviceTitle: 60,
    serviceDesc: 240,
    about: 2000,
    url: 500,
    testimonialText: 1000,
    testimonialName: 100,
};

const SOCIAL_KEYS = [
    { key: "facebook", label: "Facebook", placeholder: "https://facebook.com/yourbiz" },
    { key: "instagram", label: "Instagram", placeholder: "https://instagram.com/yourbiz" },
    { key: "tiktok", label: "TikTok", placeholder: "https://tiktok.com/@yourbiz" },
    { key: "linkedin", label: "LinkedIn", placeholder: "https://linkedin.com/company/yourbiz" },
    { key: "youtube", label: "YouTube", placeholder: "https://youtube.com/@yourbiz" },
    { key: "twitter", label: "X / Twitter", placeholder: "https://x.com/yourbiz" },
    { key: "other", label: "Other", placeholder: "Any other URL" },
];

const MAX_TESTIMONIALS_UI = 5;

const STATUS_LABELS = {
    live: { label: "Live", dot: "bg-emerald-400" },
    pending: { label: "Pending", dot: "bg-amber-400" },
    maintenance: { label: "Maintenance", dot: "bg-sky-400" },
};

/**
 * Tiny human-friendly relative time used for the "Saved X ago" cue.
 * Returns null past a generous window so we don't clutter the UI forever.
 */
function savedAgo(savedAt, now) {
    if (!savedAt) return null;
    const diff = Math.max(0, Math.round((now - savedAt) / 1000));
    if (diff < 5) return "Saved just now";
    if (diff < 60) return `Saved ${diff}s ago`;
    const mins = Math.floor(diff / 60);
    if (mins < 10) return `Saved ${mins}m ago`;
    return null; // fade out after ~10 min
}

const SectionHeader = ({ eyebrow, title, description }) => (
    <div className="mb-6">
        <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">{eyebrow}</p>
        <h2 className="mt-2 text-[20px] font-medium tracking-tight text-zinc-50">{title}</h2>
        {description && (
            <p className="mt-1.5 text-[13.5px] text-zinc-500 leading-relaxed max-w-xl">
                {description}
            </p>
        )}
    </div>
);

const Field = ({ label, hint, children }) => (
    <label className="block">
        <span className="block text-[12.5px] text-zinc-400 font-medium mb-2">{label}</span>
        {children}
        {hint && <span className="mt-1.5 block text-[11.5px] text-zinc-600">{hint}</span>}
    </label>
);

const TextInput = (props) => (
    <input
        {...props}
        className={[
            "w-full h-11 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-500",
            props.className || "",
        ].join(" ")}
    />
);

const Textarea = (props) => (
    <textarea
        {...props}
        className={[
            "w-full rounded-md bg-transparent border border-zinc-800 px-3 py-2.5 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-500 leading-relaxed",
            props.className || "",
        ].join(" ")}
    />
);

const CharCounter = ({ value, max }) => {
    const len = (value || "").length;
    const near = len > max - 10;
    return (
        <span
            className={[
                "text-[11px] tabular-nums",
                len > max ? "text-red-400" : near ? "text-amber-400" : "text-zinc-600",
            ].join(" ")}
            data-testid="char-counter"
        >
            {len}/{max}
        </span>
    );
};

export default function BusinessData() {
    const { updateBranding } = useWorkspaceBranding();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const savingRef = useRef(false);
    const loadVersion = useRef(0);
    const [areaInput, setAreaInput] = useState("");
    const [connStatus, setConnStatus] = useState(null);
    const [dirty, setDirty] = useState(false);
    useUnsavedChanges(dirty);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const [now, setNow] = useState(() => Date.now());
    // 2026-06 onboarding-form additions (declared up-front to keep hook order stable)
    const logoInputRef = useRef(null);
    const [logoBusy, setLogoBusy] = useState(false);
    const [logoBust, setLogoBust] = useState(Date.now());

    // Re-render every 15s so the "Saved Xs ago" label stays fresh, but only while a save is recent.
    useEffect(() => {
        if (!lastSavedAt) return undefined;
        const tick = setInterval(() => setNow(Date.now()), 15000);
        return () => clearInterval(tick);
    }, [lastSavedAt]);

    const loadProfile = useCallback(async () => {
        const version = ++loadVersion.current;
        setLoading(true);
        setLoadError(null);
        try {
            const [profileRes, statusRes] = await Promise.all([
                api.get("/business-profile"),
                api.get("/webhook/status").catch(() => ({ data: null })),
            ]);
            if (version !== loadVersion.current) return;
            setProfile(profileRes.data);
            if (statusRes.data) setConnStatus(statusRes.data);
        } catch (err) {
            if (version !== loadVersion.current) return;
            // Real customer impact bug (2026-05-31, Thalita Santos): if this
            // fetch fails the page used to silently stall on "Loading…"
            // forever. Now we surface a retry path so customers can recover.
            const status = err?.response?.status;
            if (status === 401) {
                setLoadError("Your session expired. Please sign in again.");
            } else if (status >= 500) {
                setLoadError("We couldn't load your business data. Please try again in a moment.");
            } else {
                setLoadError(
                    formatApiErrorDetail(err?.response?.data?.detail) ||
                    "We couldn't load your business data. Please try again."
                );
            }
        } finally {
            if (version === loadVersion.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadProfile();
        return () => { loadVersion.current += 1; };
    }, [loadProfile]);

    const hoursByDay = useMemo(() => {
        const map = Object.fromEntries(DAYS.map((d) => [d.key, { day: d.key, open: "09:00", close: "17:00", closed: false }]));
        if (profile?.hours) {
            profile.hours.forEach((h) => {
                if (map[h.day]) map[h.day] = { ...map[h.day], ...h };
            });
        }
        return map;
    }, [profile]);

    // Build-ready computation mirrors the backend's _onboarding_completion so
    // the user sees the same status before/after save. Photo-or-logo passes
    // when the client either has a logo OR has explicitly opted out.
    const buildReady = useMemo(() => {
        if (!profile) return { ready: false, percent: 0, missing: [] };
        const hasLogoOrOptOut = !!(profile.logo_media_id || profile.logo_skipped);
        const checks = {
            "Business name": !!(profile.business_name || "").trim(),
            "Phone": !!(profile.phone || "").trim(),
            "Email": !!(profile.email || "").trim(),
            "Hours": (profile.hours || []).some((h) => !h.closed),
            "Service areas": (profile.service_areas || []).length > 0,
            "Services": (profile.service_descriptions || []).filter((s) => (s.title || "").trim()).length > 0,
            "CTA text": !!(profile.cta_text || "").trim(),
            "Logo decision": hasLogoOrOptOut,
        };
        const missing = Object.entries(checks).filter(([_, v]) => !v).map(([k]) => k);
        const filled = Object.values(checks).filter(Boolean).length;
        return {
            ready: missing.length === 0,
            percent: Math.round((100 * filled) / Object.keys(checks).length),
            missing,
        };
    }, [profile]);

    if (loading) {
        return (
            <div className="mx-auto w-full max-w-3xl px-5 sm:px-8 pt-12 sm:pt-16 pb-24" data-testid="business-data-loading">
                <p className="text-[13.5px] text-zinc-500">Loading…</p>
            </div>
        );
    }
    if (loadError || !profile) {
        return (
            <div
                className="mx-auto w-full max-w-3xl px-5 sm:px-8 pt-12 sm:pt-16 pb-24"
                data-testid="business-data-error"
            >
                <div className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-5 sm:p-6 space-y-4">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 mt-0.5 text-amber-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <p className="text-[14.5px] font-medium text-zinc-100">
                                We hit a snag loading your business data.
                            </p>
                            <p className="mt-1 text-[13px] text-zinc-400">
                                {loadError || "Something went wrong. Please try again."}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={loadProfile}
                        data-testid="business-data-retry"
                        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-md bg-zinc-100 text-zinc-950 text-[13.5px] font-medium hover:bg-white transition-colors"
                    >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Try again
                    </button>
                    <p className="text-[12px] text-zinc-600">
                        If this keeps happening, please email{" "}
                        <a href="mailto:support@webjesters.com" className="text-zinc-400 underline">
                            support@webjesters.com
                        </a>{" "}
                        and we'll fix it manually.
                    </p>
                </div>
            </div>
        );
    }

    const setField = (k, v) => {
        setProfile((p) => ({ ...p, [k]: v }));
        setDirty(true);
    };

    const setHoursDay = (dayKey, patch) => {
        setProfile((p) => {
            const next = DAYS.map((d) => {
                const current = hoursByDay[d.key];
                return d.key === dayKey ? { ...current, ...patch } : current;
            });
            return { ...p, hours: next };
        });
        setDirty(true);
    };

    // Copy Monday's open/closed + open/close times to a target set of days.
    // Used by the two quick-fill buttons in the Hours section.
    const copyMondayTo = (targetKeys) => {
        const mon = hoursByDay.mon;
        if (!mon) return;
        const targets = new Set(targetKeys);
        setProfile((p) => {
            const next = DAYS.map((d) => {
                const current = hoursByDay[d.key];
                if (!targets.has(d.key)) return current;
                return { ...current, open: mon.open, close: mon.close, closed: mon.closed };
            });
            return { ...p, hours: next };
        });
        setDirty(true);
    };

    const addServiceArea = () => {
        const v = areaInput.trim();
        if (!v) return;
        if ((profile.service_areas || []).includes(v)) {
            setAreaInput("");
            return;
        }
        setProfile((p) => ({ ...p, service_areas: [...(p.service_areas || []), v] }));
        setAreaInput("");
        setDirty(true);
    };

    const removeServiceArea = (idx) => {
        setProfile((p) => ({
            ...p,
            service_areas: (p.service_areas || []).filter((_, i) => i !== idx),
        }));
        setDirty(true);
    };

    const addServiceDescription = () => {
        if ((profile.service_descriptions || []).length >= 6) return;
        setProfile((p) => ({
            ...p,
            service_descriptions: [...(p.service_descriptions || []), { title: "", description: "" }],
        }));
        setDirty(true);
    };

    const updateServiceDescription = (idx, patch) => {
        setProfile((p) => ({
            ...p,
            service_descriptions: (p.service_descriptions || []).map((sd, i) =>
                i === idx ? { ...sd, ...patch } : sd
            ),
        }));
        setDirty(true);
    };

    const removeServiceDescription = (idx) => {
        setProfile((p) => ({
            ...p,
            service_descriptions: (p.service_descriptions || []).filter((_, i) => i !== idx),
        }));
        setDirty(true);
    };

    // --- 2026-06 onboarding additions ---

    const setSocialLink = (key, value) => {
        setProfile((p) => ({
            ...p,
            social_links: { ...(p.social_links || {}), [key]: value },
        }));
        setDirty(true);
    };

    const addTestimonial = () => {
        if ((profile.testimonials || []).length >= MAX_TESTIMONIALS_UI) return;
        setProfile((p) => ({
            ...p,
            testimonials: [...(p.testimonials || []), { name: "", text: "", rating: null }],
        }));
        setDirty(true);
    };

    const updateTestimonial = (idx, patch) => {
        setProfile((p) => ({
            ...p,
            testimonials: (p.testimonials || []).map((t, i) => (i === idx ? { ...t, ...patch } : t)),
        }));
        setDirty(true);
    };

    const removeTestimonial = (idx) => {
        setProfile((p) => ({
            ...p,
            testimonials: (p.testimonials || []).filter((_, i) => i !== idx),
        }));
        setDirty(true);
    };

    const handleLogoSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast("File too large", { description: "Logo must be 10 MB or smaller." });
            return;
        }
        setLogoBusy(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const { data } = await api.post("/business-profile/logo", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setProfile(data);
            setLogoBust(Date.now());
            toast("Logo uploaded", { duration: 2000 });
        } catch (err) {
            toast("Could not upload logo", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setLogoBusy(false);
            if (logoInputRef.current) logoInputRef.current.value = "";
        }
    };

    const handleLogoRemove = async () => {
        if (!profile.logo_media_id) return;
        setLogoBusy(true);
        try {
            const { data } = await api.delete("/business-profile/logo");
            setProfile(data);
            setLogoBust(Date.now());
            toast("Logo removed", { duration: 2000 });
        } catch (err) {
            toast("Could not remove logo", {
                description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
            });
        } finally {
            setLogoBusy(false);
        }
    };

    const save = async () => {
        if (savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        try {
            const payload = {
                business_name: profile.business_name || "",
                phone: profile.phone || "",
                email: profile.email || "",
                hours: DAYS.map((d) => hoursByDay[d.key]),
                service_areas: profile.service_areas || [],
                cta_text: profile.cta_text || "",
                promo_text: profile.promo_text || "",
                service_descriptions: (profile.service_descriptions || []).filter(
                    (sd) => (sd.title || "").trim()
                ),
                about: profile.about || "",
                google_business_url: profile.google_business_url || "",
                social_links: profile.social_links || {},
                testimonials: (profile.testimonials || []).filter((t) => (t.text || "").trim()),
                logo_skipped: !!profile.logo_skipped,
            };
            const { data } = await api.patch("/business-profile", payload);
            setProfile(data);
            updateBranding(data);
            setDirty(false);
            setLastSavedAt(Date.now());
            setNow(Date.now());
            toast("Saved", {
                description: "Your business data has been updated.",
                duration: 2200,
            });
        } catch (e) {
            toast("Could not save", {
                description: formatApiErrorDetail(e.response?.data?.detail) || e.message,
                duration: 3500,
            });
        } finally {
            savingRef.current = false;
            setSaving(false);
        }
    };

    const status = STATUS_LABELS[profile.website_status] || STATUS_LABELS.pending;

    return (
        <section
            className="mx-auto w-full max-w-3xl px-5 sm:px-8 pt-12 sm:pt-16 pb-40 md:pb-32"
            data-testid="business-data-page"
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1
                        className="text-3xl sm:text-4xl font-semibold tracking-tighter text-zinc-50 inline-flex items-center gap-2.5"
                        data-testid="business-data-heading"
                    >
                        Business data
                        {dirty && (
                            <span
                                data-testid="unsaved-indicator"
                                aria-label="Unsaved changes"
                                title="Unsaved changes"
                                className="inline-block h-2 w-2 rounded-full bg-amber-400/80 shadow-[0_0_0_3px_rgba(245,158,11,0.12)]"
                            />
                        )}
                    </h1>
                    <p className="mt-2 text-[14.5px] text-zinc-500 max-w-xl">
                        The information powering your website. Changes are picked up by your live site.
                    </p>
                </div>
                <div
                    className="shrink-0 inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-950/60 px-3 py-1 text-[12px] text-zinc-300"
                    data-testid="website-status-badge"
                    title="Managed by WebJesters"
                >
                    <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                    {status.label}
                </div>
            </div>

            {/* Build Ready indicator — mirrors backend logic so the customer
                always sees the same status before/after save. */}
            <div
                data-testid="build-ready-banner"
                className={[
                    "mt-6 rounded-xl border px-4 py-3 flex items-start gap-3",
                    buildReady.ready
                        ? "border-emerald-700/40 bg-emerald-500/[0.04]"
                        : "border-zinc-800 bg-zinc-950/40",
                ].join(" ")}
            >
                {buildReady.ready ? (
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-400 shrink-0" />
                ) : (
                    <AlertCircle className="h-4 w-4 mt-0.5 text-amber-400 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] text-zinc-100 font-medium">
                        {buildReady.ready
                            ? "Build ready — WebJesters has everything needed to start your site."
                            : `Build readiness: ${buildReady.percent}%`}
                    </p>
                    {!buildReady.ready && (
                        <p className="mt-1 text-[12.5px] text-zinc-500">
                            <span data-testid="build-ready-missing">
                                Still needed: {buildReady.missing.join(", ")}
                            </span>
                        </p>
                    )}
                </div>
            </div>

            <fieldset disabled={saving} aria-busy={saving} className="mt-12 min-w-0 space-y-16" data-testid="business-profile-fields">
                {/* Section: Business */}
                <section data-testid="section-business">
                    <SectionHeader
                        eyebrow="Business"
                        title="The basics"
                        description="Your business contact details, surfaced on the site header, footer, and contact page."
                    />
                    <div className="space-y-5">
                        <Field label="Business name">
                            <TextInput
                                type="text"
                                value={profile.business_name || ""}
                                maxLength={100}
                                onChange={(e) => setField("business_name", e.target.value)}
                                data-testid="input-business-name"
                            />
                        </Field>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <Field label="Phone">
                                <TextInput
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    placeholder="(415) 555-0142"
                                    maxLength={30}
                                    value={profile.phone || ""}
                                    onChange={(e) => setField("phone", e.target.value)}
                                    data-testid="input-phone"
                                />
                            </Field>
                            <Field label="Email">
                                <TextInput
                                    type="email"
                                    inputMode="email"
                                    autoComplete="email"
                                    placeholder="hello@yourbiz.com"
                                    maxLength={120}
                                    value={profile.email || ""}
                                    onChange={(e) => setField("email", e.target.value)}
                                    data-testid="input-email"
                                />
                            </Field>
                        </div>
                    </div>
                </section>

                {/* Section: Logo */}
                <section data-testid="section-logo">
                    <SectionHeader
                        eyebrow="Brand"
                        title="Logo"
                        description="Upload your logo if you have one. If not, check the box and we'll design one as part of your build."
                    />
                    <div className="flex flex-col sm:flex-row sm:items-start gap-5">
                        <div
                            className="h-28 w-28 rounded-xl border border-zinc-800 bg-zinc-950/60 flex items-center justify-center overflow-hidden shrink-0"
                            data-testid="logo-preview"
                        >
                            {profile.logo_media_id ? (
                                <img
                                    src={`${api.defaults.baseURL}/public/tenant/${profile.tenant_id}/logo?t=${logoBust}`}
                                    alt="Logo"
                                    className="max-h-full max-w-full object-contain"
                                />
                            ) : (
                                <ImagePlus className="h-6 w-6 text-zinc-700" />
                            )}
                        </div>
                        <div className="flex-1 space-y-3">
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp,image/gif"
                                className="hidden"
                                onChange={handleLogoSelect}
                                data-testid="logo-file-input"
                            />
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => logoInputRef.current?.click()}
                                    disabled={logoBusy || profile.logo_skipped}
                                    data-testid="logo-upload-button"
                                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-800 text-[13px] text-zinc-200 hover:text-zinc-100 hover:border-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {logoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
                                    {profile.logo_media_id ? "Replace logo" : "Upload logo"}
                                </button>
                                {profile.logo_media_id && (
                                    <button
                                        type="button"
                                        onClick={handleLogoRemove}
                                        disabled={logoBusy}
                                        data-testid="logo-remove-button"
                                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-[13px] text-zinc-500 hover:text-red-400 transition-colors disabled:opacity-50"
                                    >
                                        <Trash2 className="h-3.5 w-3.5" />
                                        Remove
                                    </button>
                                )}
                            </div>
                            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    checked={!!profile.logo_skipped}
                                    onChange={(e) => setField("logo_skipped", e.target.checked)}
                                    data-testid="logo-skipped-checkbox"
                                    className="h-4 w-4 rounded border-zinc-700 bg-transparent text-zinc-100 focus:ring-zinc-500"
                                />
                                <span className="text-[13px] text-zinc-400">
                                    I do not have a logo yet — design one as part of my build.
                                </span>
                            </label>
                            <p className="text-[11.5px] text-zinc-600">
                                PNG, JPG, WEBP, or GIF · Max 10 MB.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Section: About */}
                <section data-testid="section-about">
                    <SectionHeader
                        eyebrow="About"
                        title="About your business"
                        description="A short paragraph we can use on your site's about page or homepage intro."
                    />
                    <Field
                        label={
                            <span className="flex items-center justify-between gap-3">
                                <span>Tell us briefly about your business, what you do, and what makes you different.</span>
                                <CharCounter value={profile.about} max={LIMITS.about} />
                            </span>
                        }
                    >
                        <Textarea
                            rows={5}
                            maxLength={LIMITS.about}
                            placeholder="Family-owned plumbing service serving the Chandler area since 1998. Same-day emergencies. Honest pricing. No upselling."
                            value={profile.about || ""}
                            onChange={(e) => setField("about", e.target.value)}
                            data-testid="input-about"
                        />
                    </Field>
                </section>

                {/* Section: Hours */}
                <section data-testid="section-hours">
                    <SectionHeader
                        eyebrow="Hours"
                        title="When you're open"
                        description="Set each day individually. Toggle 'Closed' for days you don't take work."
                    />
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                copyMondayTo(["tue", "wed", "thu", "fri"]);
                                toast("Copied Monday to Tue–Fri", { duration: 1500 });
                            }}
                            data-testid="hours-copy-weekdays"
                            className="inline-flex items-center h-9 px-3 rounded-md border border-zinc-800 text-[12.5px] text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                        >
                            Copy Monday to weekdays
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                copyMondayTo(["tue", "wed", "thu", "fri", "sat", "sun"]);
                                toast("Copied Monday to every day", { duration: 1500 });
                            }}
                            data-testid="hours-copy-all"
                            className="inline-flex items-center h-9 px-3 rounded-md border border-zinc-800 text-[12.5px] text-zinc-300 hover:text-zinc-100 hover:border-zinc-700 transition-colors"
                        >
                            Copy Monday to all days
                        </button>
                    </div>
                    <div className="rounded-xl border border-zinc-800 divide-y divide-zinc-800/80 overflow-hidden">
                        {DAYS.map(({ key, label }) => {
                            const h = hoursByDay[key];
                            return (
                                <div
                                    key={key}
                                    // Mobile: 2-col grid where the day label + Closed toggle sit on
                                    // the top row and the time inputs flow full-width on row 2.
                                    // Desktop: classic 3-col row [day | times | Closed].
                                    className="grid grid-cols-[1fr_auto] sm:grid-cols-[120px_1fr_auto] items-center gap-x-3 gap-y-3 sm:gap-4 px-4 sm:px-5 py-3.5"
                                    data-testid={`hours-row-${key}`}
                                >
                                    <p className="text-[13.5px] text-zinc-300 sm:col-start-1 sm:row-start-1">
                                        {label}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setHoursDay(key, { closed: !h.closed })}
                                        data-testid={`hours-closed-${key}`}
                                        className={[
                                            "shrink-0 h-9 sm:h-8 px-3 rounded-md border text-[12.5px] transition-colors min-w-[88px]",
                                            "sm:col-start-3 sm:row-start-1",
                                            h.closed
                                                ? "border-amber-700/50 bg-amber-950/30 text-amber-200"
                                                : "border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700",
                                        ].join(" ")}
                                        aria-pressed={h.closed}
                                    >
                                        {h.closed ? "Closed" : "Open"}
                                    </button>
                                    <div
                                        className="col-span-2 sm:col-span-1 sm:col-start-2 sm:row-start-1 flex items-center gap-2"
                                        aria-hidden={h.closed}
                                    >
                                        <input
                                            type="time"
                                            disabled={h.closed}
                                            value={h.open}
                                            onChange={(e) => setHoursDay(key, { open: e.target.value })}
                                            data-testid={`hours-open-${key}`}
                                            className="h-10 sm:h-9 flex-1 sm:flex-none sm:w-[110px] min-w-0 rounded-md bg-transparent border border-zinc-800 px-2.5 text-[13px] text-zinc-100 outline-none transition-colors focus:border-zinc-500 disabled:opacity-40"
                                        />
                                        <span className="text-zinc-600 text-[12px] shrink-0">to</span>
                                        <input
                                            type="time"
                                            disabled={h.closed}
                                            value={h.close}
                                            onChange={(e) => setHoursDay(key, { close: e.target.value })}
                                            data-testid={`hours-close-${key}`}
                                            className="h-10 sm:h-9 flex-1 sm:flex-none sm:w-[110px] min-w-0 rounded-md bg-transparent border border-zinc-800 px-2.5 text-[13px] text-zinc-100 outline-none transition-colors focus:border-zinc-500 disabled:opacity-40"
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Section: Service areas */}
                <section data-testid="section-service-areas">
                    <SectionHeader
                        eyebrow="Service areas"
                        title="Where you work"
                        description="Cities or neighborhoods you serve. These appear on your service-area pages."
                    />
                    <div>
                        <div className="flex gap-2">
                            <TextInput
                                type="text"
                                placeholder="e.g. San Francisco"
                                maxLength={60}
                                value={areaInput}
                                onChange={(e) => setAreaInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        addServiceArea();
                                    }
                                }}
                                data-testid="input-service-area"
                            />
                            <button
                                type="button"
                                onClick={addServiceArea}
                                data-testid="add-service-area-button"
                                className="shrink-0 h-11 px-4 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-100 text-[13px] font-medium hover:bg-zinc-800 transition-colors inline-flex items-center gap-1.5"
                            >
                                <Plus className="h-4 w-4" />
                                Add
                            </button>
                        </div>
                        {(profile.service_areas || []).length > 0 && (
                            <ul className="mt-4 flex flex-wrap gap-2" data-testid="service-areas-list">
                                {(profile.service_areas || []).map((area, idx) => (
                                    <li
                                        key={`${area}-${idx}`}
                                        className="inline-flex items-center gap-1.5 h-8 pl-3 pr-1.5 rounded-full border border-zinc-800 bg-zinc-950/60 text-[12.5px] text-zinc-200"
                                    >
                                        {area}
                                        <button
                                            type="button"
                                            onClick={() => removeServiceArea(idx)}
                                            aria-label={`Remove ${area}`}
                                            data-testid={`remove-service-area-${idx}`}
                                            className="h-5 w-5 inline-flex items-center justify-center rounded-full text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
                                        >
                                            <X className="h-3 w-3" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </section>

                {/* Section: Marketing copy */}
                <section data-testid="section-marketing">
                    <SectionHeader
                        eyebrow="Marketing"
                        title="On-site copy"
                        description="Short snippets that anchor your homepage call-to-action and promo banner."
                    />
                    <div className="space-y-5">
                        <Field label={
                            <span className="flex items-center justify-between gap-3">
                                <span>Call-to-action text</span>
                                <CharCounter value={profile.cta_text} max={LIMITS.cta} />
                            </span>
                        }>
                            <TextInput
                                type="text"
                                placeholder="Get a free quote today"
                                maxLength={LIMITS.cta}
                                value={profile.cta_text || ""}
                                onChange={(e) => setField("cta_text", e.target.value)}
                                data-testid="input-cta-text"
                            />
                        </Field>
                        <Field label={
                            <span className="flex items-center justify-between gap-3">
                                <span>Promo banner text</span>
                                <CharCounter value={profile.promo_text} max={LIMITS.promo} />
                            </span>
                        } hint="Used on seasonal/limited-time banners. Leave empty to hide the banner.">
                            <Textarea
                                rows={2}
                                maxLength={LIMITS.promo}
                                placeholder="20% off drain cleaning through April."
                                value={profile.promo_text || ""}
                                onChange={(e) => setField("promo_text", e.target.value)}
                                data-testid="input-promo-text"
                            />
                        </Field>
                    </div>
                </section>

                {/* Section: Trust & Reach */}
                <section data-testid="section-google-business">
                    <SectionHeader
                        eyebrow="Trust"
                        title="Google Business Profile"
                        description="Optional. Paste your Google listing URL so we can pull your existing reviews and trust signals into your new site."
                    />
                    <Field label="Google Business Profile URL">
                        <TextInput
                            type="url"
                            inputMode="url"
                            placeholder="https://g.page/your-business"
                            maxLength={LIMITS.url}
                            value={profile.google_business_url || ""}
                            onChange={(e) => setField("google_business_url", e.target.value)}
                            data-testid="input-google-business-url"
                        />
                    </Field>
                </section>

                {/* Section: Social Links */}
                <section data-testid="section-social-links">
                    <SectionHeader
                        eyebrow="Social"
                        title="Social media"
                        description="Optional. Any platforms you're active on — we'll link them in your site footer."
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {SOCIAL_KEYS.map((s) => (
                            <Field key={s.key} label={s.label}>
                                <TextInput
                                    type="url"
                                    inputMode="url"
                                    placeholder={s.placeholder}
                                    maxLength={LIMITS.url}
                                    value={(profile.social_links || {})[s.key] || ""}
                                    onChange={(e) => setSocialLink(s.key, e.target.value)}
                                    data-testid={`social-${s.key}`}
                                />
                            </Field>
                        ))}
                    </div>
                </section>

                {/* Section: Testimonials */}
                <section data-testid="section-testimonials">
                    <SectionHeader
                        eyebrow="Proof"
                        title="Reviews & testimonials"
                        description={`Optional. Paste up to ${MAX_TESTIMONIALS_UI} reviews from happy customers — we'll style them on your site.`}
                    />
                    <div className="space-y-3">
                        {(profile.testimonials || []).map((t, idx) => (
                            <div
                                key={idx}
                                className="rounded-xl border border-zinc-800 p-4 sm:p-5 space-y-3"
                                data-testid={`testimonial-row-${idx}`}
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-3 items-center">
                                    <input
                                        type="text"
                                        placeholder="Customer name (optional)"
                                        maxLength={LIMITS.testimonialName}
                                        value={t.name || ""}
                                        onChange={(e) => updateTestimonial(idx, { name: e.target.value })}
                                        data-testid={`testimonial-name-${idx}`}
                                        className="h-10 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-500"
                                    />
                                    <div
                                        className="inline-flex items-center gap-1"
                                        data-testid={`testimonial-rating-${idx}`}
                                    >
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <button
                                                key={n}
                                                type="button"
                                                onClick={() =>
                                                    updateTestimonial(idx, {
                                                        rating: t.rating === n ? null : n,
                                                    })
                                                }
                                                aria-label={`Rate ${n} stars`}
                                                data-testid={`testimonial-rating-${idx}-${n}`}
                                                className="p-1 rounded hover:bg-zinc-900 transition-colors"
                                            >
                                                <Star
                                                    className={[
                                                        "h-4 w-4",
                                                        (t.rating || 0) >= n
                                                            ? "fill-amber-400 text-amber-400"
                                                            : "text-zinc-700",
                                                    ].join(" ")}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeTestimonial(idx)}
                                        aria-label="Remove testimonial"
                                        data-testid={`testimonial-remove-${idx}`}
                                        className="h-9 w-9 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <Textarea
                                    rows={3}
                                    placeholder="What the customer said about your business."
                                    maxLength={LIMITS.testimonialText}
                                    value={t.text || ""}
                                    onChange={(e) => updateTestimonial(idx, { text: e.target.value })}
                                    data-testid={`testimonial-text-${idx}`}
                                />
                                <div className="flex justify-end">
                                    <CharCounter value={t.text} max={LIMITS.testimonialText} />
                                </div>
                            </div>
                        ))}
                        {(profile.testimonials || []).length < MAX_TESTIMONIALS_UI && (
                            <button
                                type="button"
                                onClick={addTestimonial}
                                data-testid="add-testimonial-button"
                                className="w-full h-11 rounded-md border border-dashed border-zinc-800 text-[13px] text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 hover:bg-zinc-950 transition-colors inline-flex items-center justify-center gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add testimonial
                            </button>
                        )}
                    </div>
                </section>

                {/* Section: Services */}
                <section data-testid="section-services">
                    <SectionHeader
                        eyebrow="Services"
                        title="What you offer"
                        description="Short descriptions for each service. Up to 6."
                    />
                    <div className="space-y-3">
                        {(profile.service_descriptions || []).map((sd, idx) => (
                            <div
                                key={idx}
                                className="rounded-xl border border-zinc-800 p-4 sm:p-5 space-y-3"
                                data-testid={`service-row-${idx}`}
                            >
                                <div className="flex items-center gap-3">
                                    <input
                                        type="text"
                                        placeholder="Service title"
                                        maxLength={LIMITS.serviceTitle}
                                        value={sd.title || ""}
                                        onChange={(e) => updateServiceDescription(idx, { title: e.target.value })}
                                        data-testid={`service-title-${idx}`}
                                        className="flex-1 h-10 rounded-md bg-transparent border border-zinc-800 px-3 text-[14px] text-zinc-100 placeholder-zinc-600 outline-none transition-colors focus:border-zinc-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeServiceDescription(idx)}
                                        aria-label="Remove service"
                                        data-testid={`remove-service-${idx}`}
                                        className="h-9 w-9 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-900 transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <Textarea
                                    rows={2}
                                    placeholder="Short description (what's included, who it's for)"
                                    maxLength={LIMITS.serviceDesc}
                                    value={sd.description || ""}
                                    onChange={(e) => updateServiceDescription(idx, { description: e.target.value })}
                                    data-testid={`service-description-${idx}`}
                                />
                                <div className="flex justify-end">
                                    <CharCounter value={sd.description} max={LIMITS.serviceDesc} />
                                </div>
                            </div>
                        ))}
                        {(profile.service_descriptions || []).length < 6 && (
                            <button
                                type="button"
                                onClick={addServiceDescription}
                                data-testid="add-service-button"
                                className="w-full h-11 rounded-md border border-dashed border-zinc-800 text-[13px] text-zinc-400 hover:text-zinc-100 hover:border-zinc-700 hover:bg-zinc-950 transition-colors inline-flex items-center justify-center gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add service
                            </button>
                        )}
                    </div>
                </section>
                {/* Section: Connection status (client-friendly abstraction) */}
                {connStatus && <ConnectionStatusSection status={connStatus} />}

            </fieldset>

            {/* Sticky save bar — on mobile, sits ABOVE the bottom nav (h-16) so it stays tappable. */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="portal-savebar fixed inset-x-0 md:relative md:mt-12 md:inset-auto z-[60] md:z-auto"
                data-testid="business-save-bar"
            >
                <div className="md:hidden h-12 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/95 to-transparent pointer-events-none absolute inset-x-0 -top-12" />
                <div className="bg-[#0a0a0a]/95 backdrop-blur-md md:backdrop-blur-0 md:bg-transparent border-t md:border-0 border-zinc-800/80 px-5 sm:px-8 py-3 md:py-0 flex items-center gap-3 justify-between md:justify-start">
                    {(() => {
                        const cue = saving ? "Saving…" : dirty ? "Unsaved changes" : savedAgo(lastSavedAt, now) || "All changes saved";
                        return (
                            <motion.span
                                key={cue || "idle"}
                                initial={cue ? { opacity: 0, y: 4 } : false}
                                animate={cue ? { opacity: 1, y: 0 } : { opacity: 0 }}
                                transition={{ duration: 0.35 }}
                                data-testid="saved-indicator"
                                aria-live="polite"
                                className={`portal-save-indicator inline-flex items-center gap-1.5 md:order-2 md:ml-3 ${dirty ? "text-amber-300" : "text-zinc-400"}`}
                            >
                                {cue && (
                                    <>
                                        <span className={`h-1.5 w-1.5 rounded-full ${dirty ? "bg-amber-400" : "bg-emerald-400/80"}`} />
                                        {cue}
                                    </>
                                )}
                            </motion.span>
                        );
                    })()}
                    <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        data-testid="save-button"
                        className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-zinc-50 text-zinc-950 text-[14px] font-medium hover:bg-white transition-colors disabled:opacity-70 disabled:cursor-not-allowed md:order-1"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Saving
                            </>
                        ) : (
                            "Save changes"
                        )}
                    </button>
                </div>
            </motion.div>

            <Toaster
                theme="dark"
                position="bottom-center"
                toastOptions={{
                    style: {
                        background: "#0a0a0a",
                        border: "1px solid #27272a",
                        color: "#f4f4f5",
                    },
                }}
            />
        </section>
    );
}

function ConnectionStatusSection({ status }) {
    const lastLead = status.last_lead_at;
    const relative = (iso) => {
        if (!iso) return "None yet";
        const d = new Date(iso);
        const mins = Math.round((Date.now() - d.getTime()) / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.round(hrs / 24);
        if (days < 7) return `${days}d ago`;
        return d.toLocaleDateString();
    };

    const rows = [
        {
            label: "Website connection",
            value: status.connection_active ? "Active" : "Standby",
            ok: status.connection_active,
            testid: "status-connection",
        },
        {
            label: "Lead sync",
            value: status.sync_healthy ? "Healthy" : "Attention needed",
            ok: status.sync_healthy,
            testid: "status-sync",
        },
        {
            label: "Last lead received",
            value: lastLead ? relative(lastLead) : "None yet",
            ok: null,
            testid: "status-last-lead",
        },
    ];

    return (
        <section data-testid="section-connection-status">
            <SectionHeader
                eyebrow="Connection"
                title="Site & sync status"
                description="Your website's link to WebJesters. Everything technical is handled on our side."
            />
            <div className="rounded-xl border border-zinc-800 divide-y divide-zinc-800/80 overflow-hidden">
                {rows.map((r) => (
                    <div
                        key={r.testid}
                        className="flex items-center justify-between gap-4 px-5 py-4"
                        data-testid={r.testid}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            {r.ok === null ? (
                                <Activity className="h-3.5 w-3.5 text-zinc-500" />
                            ) : (
                                <span
                                    className={[
                                        "h-2 w-2 rounded-full shrink-0",
                                        r.ok ? "bg-emerald-400" : "bg-amber-400",
                                    ].join(" ")}
                                />
                            )}
                            <span className="text-[14px] text-zinc-300">{r.label}</span>
                        </div>
                        <span className="text-[13.5px] text-zinc-100 truncate">{r.value}</span>
                    </div>
                ))}
            </div>
        </section>
    );
}
